/**
 * BifrostBridge - Singleton that manages native bridge communication
 *
 * This class handles the actual WebView bridge connection and coordinates
 * message passing between multiple BifrostWeb client instances.
 */

import {
  RpcEnvelope,
  RpcResponse,
  CallOptions,
  BifrostChunk,
  DEFAULT_READY_TIMEOUT_MS,
} from "../types/protocol";
import {
  DeliveryUnknownError,
  OverloadedError,
  CallCancelledError,
  CallDeadlineExceededError,
  SessionReplacedError,
  RpcCallError,
  TransportNotAdmittedError,
} from "../types/errors";
import { ChunkAssembler, DEFAULT_TRANSFER_TIMEOUT_MS } from "./ChunkAssembler";
import {
  assertValidChunkConfig,
  ChunkConfig,
  ChunkSender,
  prepareMessage,
} from "./ChunkSender";
import { NativeHandshake } from "./NativeHandshake";
import { callDeadlineMs, chunkTransferAllowanceMs } from "./callDeadline";
import { V2Session } from "./V2Session";

// Forward declaration for BifrostWeb
export interface IBifrostClient {
  readonly clientId: string;
  readonly isDestroyed: boolean;
  hasService(serviceName: string): boolean;
  handleIncomingCall(envelope: RpcEnvelope): Promise<void>;
  handleIncomingNotification(envelope: RpcEnvelope): void;
  /**
   * The shared handshake completed. Called on every registered client, including
   * ones whose own `initialize()` already rejected: the watch they started can be
   * extended by a later caller and complete after they gave up, leaving them
   * unusable against a working bridge.
   *
   * This does not replay a call that already failed — only makes subsequent ones
   * possible without a second `initialize()`.
   */
  markBridgeReady?(): void;
}

const DEFAULT_CALL_TIMEOUT_MS = 5000;

function advertisedDirectLimit(
  nativeBridge = typeof window === "undefined"
    ? undefined
    : window.bifrostNativeBridge,
): number | undefined {
  const limit = nativeBridge?.maxUnchunkedMessageBytes;
  return typeof limit === "number" && Number.isSafeInteger(limit) && limit > 0
    ? limit
    : undefined;
}

interface PendingCall {
  resolve: (value: any) => void;
  reject: (error: Error) => void;
  timeout?: ReturnType<typeof setTimeout>;
  clientId: string; // Track which client initiated this call
  deadlineAt: number;
  canExtendResponseDeadline: boolean;
}

interface AdmissionWaiter {
  deadlineAt: number;
  localLimit: number;
  resolve: (release: () => void) => void;
  reject: (error: Error) => void;
  timer: ReturnType<typeof setTimeout>;
  signal?: AbortSignal;
  abort?: () => void;
}

/**
 * Singleton bridge that manages communication with the native side
 */
export class BifrostBridge {
  private static instance: BifrostBridge;

  private clients = new Map<string, IBifrostClient>();
  private pendingCalls = new Map<string, PendingCall>();
  private handlerInitialized = false;
  private isDestroyed = false;
  private v2Session?: V2Session;
  private droppedV2Notifications = 0;
  private admittedCalls = 0;
  private admissionWaiters: AdmissionWaiter[] = [];

  /**
   * Owns the handshake outright: which shim a send and an ack belong to, host
   * re-entrancy, the deadline, the shared attempt, and when evaluation is scheduled.
   * This class only awaits an outcome, forwards `native_ready`, and stops it.
   */
  private handshake = new NativeHandshake({
    isDestroyed: () => this.isDestroyed,
    onCompleted: () => this.markClientsReady(),
  });
  private chunkAssembler = new ChunkAssembler({
    onAccept: (chunk) => this.extendResponseDeadline(chunk),
    onReject: (transferId, reason) => {
      // An inbound transfer that dies must say so. Without this the message
      // simply never arrives and the caller can only infer it from a timeout.
      // eslint-disable-next-line no-console
      console.error(
        `[BifrostBridge] Inbound multipart transfer ${
          transferId.length > 128 ? `${transferId.slice(0, 128)}…` : transferId
        } rejected: ${reason}`,
      );
    },
  });
  private chunkSender: ChunkSender | null = null;
  private chunkConfig: ChunkConfig = {};

  private constructor() {
    this.setupGlobalHandler();
  }

  /**
   * Get the singleton instance
   */
  static getInstance(): BifrostBridge {
    if (!BifrostBridge.instance || BifrostBridge.instance.isDestroyed) {
      BifrostBridge.instance = new BifrostBridge();
    }
    return BifrostBridge.instance;
  }

  /**
   * Initialize the bridge connection
   */
  async initialize(
    readyTimeout: number = DEFAULT_READY_TIMEOUT_MS,
  ): Promise<void> {
    if (this.isDestroyed) {
      throw new Error("BifrostBridge has been destroyed");
    }
    const v2Adapter =
      typeof window === "undefined" ? undefined : window.bifrostV2Bootstrap;
    if (v2Adapter) {
      this.v2Session ??= new V2Session(
        v2Adapter,
        (message) => void this.handleGlobalMessage(message),
        () => this.handleV2Fault(),
        (chunk) => this.extendResponseDeadline(chunk),
      );
      if (await this.v2Session.initialize(readyTimeout)) {
        this.markClientsReady();
        return;
      }
      this.v2Session.close();
      this.v2Session = undefined;
    }
    await this.handshake.await(readyTimeout);
  }

  /**
   * Register a client with the bridge
   */
  registerClient(client: IBifrostClient): void {
    if (this.isDestroyed) {
      throw new Error("BifrostBridge has been destroyed");
    }

    this.clients.set(client.clientId, client);
  }

  /**
   * Unregister a client from the bridge
   */
  unregisterClient(clientId: string): void {
    // Remove client
    this.clients.delete(clientId);

    // Cancel any pending calls for this client
    for (const [callId, pendingCall] of this.pendingCalls) {
      if (pendingCall.clientId === clientId) {
        if (pendingCall.timeout) clearTimeout(pendingCall.timeout);
        pendingCall.reject(new Error("Client instance destroyed"));
        this.pendingCalls.delete(callId);
      }
    }

    // If no more clients, cleanup global handler
    if (this.clients.size === 0) {
      this.cleanup();
    }
  }

  /**
   * Send an RPC call through the bridge
   */
  async sendCall(
    clientId: string,
    callId: string,
    service: string,
    method: string,
    payload: any,
    options?: CallOptions,
    envelope?: RpcEnvelope,
    chunkConfig: ChunkConfig = this.chunkConfig,
  ): Promise<any> {
    if (this.isDestroyed) {
      throw new Error("BifrostBridge has been destroyed");
    }

    // Use provided envelope or create a new one
    const finalEnvelope: RpcEnvelope = envelope || {
      id: callId,
      kind: "call",
      service,
      method,
      payload,
      ts: Date.now(),
    };
    const sizeBytes = this.prepareCallMessage(
      finalEnvelope,
      options,
      chunkConfig,
    );
    const chunkSize = this.v2Session?.isReady()
      ? this.v2Session.multipartChunkSize()
      : (chunkConfig.chunkSize ?? 128 * 1024);

    return new Promise<any>((resolve, reject) => {
      // A chunked message needs longer than the base deadline; at 5s the
      // advertised maxTransferSize is unreachable by construction, so a large
      // payload would always time out however healthy the transport is. An
      // explicit caller timeout still wins.
      this.pendingCalls.set(callId, {
        resolve,
        reject,
        clientId,
        deadlineAt: 0,
        canExtendResponseDeadline: options?.timeout === undefined,
      });

      this.sendToNative(
        finalEnvelope,
        undefined,
        (chunkThreshold) => {
          const pending = this.pendingCalls.get(callId);
          if (!pending) return;
          const baseDeadline =
            options?.timeout ??
            (finalEnvelope.deadlineAt === undefined
              ? DEFAULT_CALL_TIMEOUT_MS
              : Math.max(0, finalEnvelope.deadlineAt - Date.now()));
          const deadline =
            options?.timeout ??
            (finalEnvelope.deadlineAt === undefined
              ? callDeadlineMs(
                  sizeBytes,
                  chunkThreshold,
                  chunkSize,
                  baseDeadline,
                )
              : baseDeadline);
          const pendingDeadlineAt =
            finalEnvelope.deadlineAt ?? Date.now() + deadline;
          pending.deadlineAt = pendingDeadlineAt;
          pending.timeout = setTimeout(
            () => {
              this.pendingCalls.delete(callId);
              reject(
                this.v2Session?.isReady()
                  ? new DeliveryUnknownError()
                  : new Error("RPC call timeout"),
              );
            },
            Math.max(0, pendingDeadlineAt - Date.now()),
          );
        },
        () => this.pendingCalls.has(callId),
        callId,
        chunkConfig,
      );
    });
  }

  private prepareCallMessage(
    envelope: RpcEnvelope,
    options: CallOptions | undefined,
    chunkConfig: ChunkConfig,
  ): number {
    const originalDeadlineAt = envelope.deadlineAt;
    let sizeBytes = prepareMessage(envelope);
    if (options?.timeout === undefined && originalDeadlineAt !== undefined) {
      const now = Date.now();
      const baseMs = Math.max(0, originalDeadlineAt - now);
      const { chunkThreshold, chunkSize } =
        this.callTransportLimits(chunkConfig);
      for (let attempt = 0; attempt < 3; attempt++) {
        const allowance =
          callDeadlineMs(sizeBytes, chunkThreshold, chunkSize, baseMs) - baseMs;
        const adjustedDeadlineAt = originalDeadlineAt + allowance;
        if (envelope.deadlineAt === adjustedDeadlineAt) break;
        envelope.deadlineAt = adjustedDeadlineAt;
        sizeBytes = prepareMessage(envelope);
      }
    }
    return sizeBytes;
  }

  private callTransportLimits(chunkConfig: ChunkConfig): {
    chunkThreshold: number;
    chunkSize: number;
  } {
    if (this.v2Session?.isReady()) {
      return {
        chunkThreshold: this.v2Session.directLimit(),
        chunkSize: this.v2Session.multipartChunkSize(),
      };
    }
    const nativeBridge =
      typeof window === "undefined" ? undefined : window.bifrostNativeBridge;
    return {
      chunkThreshold:
        chunkConfig.chunkThreshold ??
        advertisedDirectLimit(nativeBridge) ??
        256 * 1024,
      chunkSize: chunkConfig.chunkSize ?? 128 * 1024,
    };
  }

  admitCall(
    deadlineAt: number,
    localLimit: number,
    signal?: AbortSignal,
  ): Promise<() => void> {
    if (signal?.aborted) return Promise.reject(new CallCancelledError());
    const capacity = Math.min(
      localLimit,
      this.v2Session?.admissionWindow() || localLimit,
    );
    if (this.admittedCalls < capacity && this.admissionWaiters.length === 0) {
      this.admittedCalls++;
      return Promise.resolve(this.releaseAdmission());
    }
    if (this.admissionWaiters.length >= localLimit)
      return Promise.reject(new OverloadedError());
    return new Promise((resolve, reject) => {
      const waiter: AdmissionWaiter = {
        deadlineAt,
        localLimit,
        resolve,
        reject,
        timer: setTimeout(
          () => {
            this.removeWaiter(waiter);
            reject(new CallDeadlineExceededError());
          },
          Math.max(0, deadlineAt - Date.now()),
        ),
        signal,
      };
      waiter.abort = () => {
        this.removeWaiter(waiter);
        reject(new CallCancelledError());
      };
      signal?.addEventListener("abort", waiter.abort, { once: true });
      this.admissionWaiters.push(waiter);
    });
  }

  async recoverV2(deadlineAt: number): Promise<void> {
    if (!this.v2Session || this.v2Session.isReady()) return;
    const recovered = await this.v2Session.recover(
      Math.max(0, deadlineAt - Date.now()),
    );
    if (!recovered) throw new SessionReplacedError();
  }

  /**
   * Send a notification through the bridge
   */
  sendNotification(
    service: string,
    method: string,
    payload: any,
    callId: string,
    chunkConfig: ChunkConfig = this.chunkConfig,
  ): void {
    if (this.isDestroyed) {
      return;
    }
    if (
      this.v2Session?.isReady() &&
      this.admittedCalls >= this.v2Session.admissionWindow()
    ) {
      this.droppedV2Notifications = Math.min(
        Number.MAX_SAFE_INTEGER,
        this.droppedV2Notifications + 1,
      );
      return;
    }

    const envelope: RpcEnvelope = {
      id: callId,
      kind: "notify",
      service,
      method,
      payload,
      ts: Date.now(),
    };

    this.sendToNative(
      envelope,
      undefined,
      undefined,
      undefined,
      undefined,
      chunkConfig,
    );
  }

  /**
   * Send a response back to native
   */
  sendResponse(
    response: RpcResponse,
    chunkConfig: ChunkConfig = this.chunkConfig,
  ): void {
    if (this.isDestroyed) {
      return;
    }

    this.sendToNative(
      response,
      response.id,
      undefined,
      undefined,
      undefined,
      chunkConfig,
    );
  }

  /**
   * Setup global message handler
   */
  private setupGlobalHandler(): void {
    if (this.handlerInitialized || typeof window === "undefined") {
      return;
    }

    window.bifrostHandleN2W = (message: any) => {
      this.handleGlobalMessage(message);
    };

    this.handlerInitialized = true;
  }

  /**
   * Handle incoming messages from native
   */
  private async handleGlobalMessage(message: any): Promise<void> {
    if (this.isDestroyed) {
      return;
    }

    try {
      // Multipart chunk handling — reassemble before dispatching
      if (this.isChunk(message)) {
        const assembled = this.chunkAssembler.addChunk(message);
        if (assembled) {
          const reassembled = JSON.parse(assembled);
          if (
            message.responseId !== undefined &&
            (!this.isResponse(reassembled) ||
              reassembled.id !== message.responseId)
          ) {
            throw new Error("Chunk responseId does not match response id");
          }
          await this.handleGlobalMessage(reassembled);
        }
        return;
      }

      if (this.isResponse(message)) {
        this.handleResponse(message);
      } else if (this.isCall(message)) {
        await this.handleIncomingCall(message);
      } else if (this.isNotification(message)) {
        this.handleIncomingNotification(message);
      } else if (this.handshake.isNativeReady(message)) {
        this.handshake.handleNativeReady(message);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("[BifrostBridge] Error handling global message:", error);
    }
  }

  /**
   * Handle response messages
   */
  private handleResponse(response: RpcResponse): void {
    const pendingCall = this.pendingCalls.get(response.id);
    if (!pendingCall) {
      return; // Response for unknown call, might be timeout
    }

    this.pendingCalls.delete(response.id);
    if (pendingCall.timeout) clearTimeout(pendingCall.timeout);

    if (response.error) {
      const error = new RpcCallError(response.error);
      pendingCall.reject(error);
    } else {
      pendingCall.resolve(response.result);
    }
  }

  private extendResponseDeadline(chunk: BifrostChunk): void {
    const responseId = chunk.responseId;
    if (!responseId) {
      return;
    }

    const pendingCall = this.pendingCalls.get(responseId);
    if (!pendingCall?.canExtendResponseDeadline) {
      return;
    }

    pendingCall.canExtendResponseDeadline = false;
    pendingCall.deadlineAt += Math.min(
      chunkTransferAllowanceMs(chunk.total),
      DEFAULT_TRANSFER_TIMEOUT_MS,
    );
    if (pendingCall.timeout) clearTimeout(pendingCall.timeout);
    pendingCall.timeout = setTimeout(
      () => {
        this.pendingCalls.delete(responseId);
        pendingCall.reject(new Error("RPC call timeout"));
      },
      Math.max(0, pendingCall.deadlineAt - Date.now()),
    );
  }

  /**
   * Handle incoming calls from native
   */
  private async handleIncomingCall(envelope: RpcEnvelope): Promise<void> {
    if (this.v2Session?.isReady()) {
      const owners = [...this.clients.values()].filter(
        (client) => !client.isDestroyed && client.hasService(envelope.service!),
      );
      if (owners.length !== 1) {
        this.sendResponse({
          id: envelope.id,
          ok: false,
          error: {
            code: owners.length === 0 ? 3001 : 4004,
            message:
              owners.length === 0 ? "Service not found" : "Service conflict",
          },
          ts: Date.now(),
        });
        return;
      }
      await owners[0].handleIncomingCall(envelope);
      return;
    }
    // Broadcast to all active clients (original behavior)
    for (const client of this.clients.values()) {
      if (!client.isDestroyed) {
        try {
          await client.handleIncomingCall(envelope);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error("[BifrostBridge] Error routing call to client:", error);
        }
      }
    }
  }

  /**
   * Handle incoming notifications from native
   */
  private handleIncomingNotification(envelope: RpcEnvelope): void {
    if (this.v2Session?.isReady()) {
      const owner = [...this.clients.values()].find(
        (client) => !client.isDestroyed && client.hasService(envelope.service!),
      );
      owner?.handleIncomingNotification(envelope);
      return;
    }
    // Broadcast notifications to all active clients
    for (const client of this.clients.values()) {
      if (!client.isDestroyed) {
        try {
          client.handleIncomingNotification(envelope);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(
            "[BifrostBridge] Error routing notification to client:",
            error,
          );
        }
      }
    }
  }

  /**
   * Send message to native bridge, chunking if necessary
   */
  private sendToNative(
    message: RpcEnvelope | RpcResponse,
    responseId?: string,
    onStart?: (chunkThreshold: number) => void,
    shouldSend?: () => boolean,
    pendingCallId?: string,
    chunkConfig: ChunkConfig = this.chunkConfig,
  ): void {
    if (this.v2Session?.isReady()) {
      try {
        const directLimit = this.v2Session.directLimit();
        onStart?.(directLimit);
        if (shouldSend?.() === false) return;
        if (this.v2Session.send(message) === "not_admitted") {
          if ("kind" in message && message.kind === "notify") {
            this.droppedV2Notifications = Math.min(
              Number.MAX_SAFE_INTEGER,
              this.droppedV2Notifications + 1,
            );
            return;
          }
          throw new TransportNotAdmittedError();
        }
      } catch (error) {
        const failure =
          error instanceof Error ? error : new Error(String(error));
        const pending = pendingCallId
          ? this.pendingCalls.get(pendingCallId)
          : undefined;
        if (pending && pendingCallId) {
          if (pending.timeout) clearTimeout(pending.timeout);
          this.pendingCalls.delete(pendingCallId);
          pending.reject(failure);
        }
      }
      return;
    }
    const configuredThreshold = chunkConfig.chunkThreshold;
    const chunkSender = this.getChunkSender();
    chunkSender.setConfig(chunkConfig);
    chunkSender
      .sendMessage(message, responseId, onStart, shouldSend, () => {
        const nativeBridge = window.bifrostNativeBridge;
        return {
          chunkThreshold:
            configuredThreshold ??
            advertisedDirectLimit(nativeBridge) ??
            256 * 1024,
          send: (msg: any, serialized?: string) => {
            if (this.isDestroyed) {
              throw new Error("BifrostBridge has been destroyed");
            }
            if (!nativeBridge) {
              throw new Error("Native bridge not available");
            }
            if (window.bifrostNativeBridge !== nativeBridge) {
              throw new Error("Native bridge changed during message send");
            }
            if (
              serialized !== undefined &&
              nativeBridge.sendSerializedToNative
            ) {
              nativeBridge.sendSerializedToNative(serialized);
            } else {
              nativeBridge.sendToNative(msg);
            }
          },
        };
      })
      .catch((error: unknown) => {
        const failure =
          error instanceof Error ? error : new Error(String(error));
        const pending = pendingCallId
          ? this.pendingCalls.get(pendingCallId)
          : undefined;

        if (pending && pendingCallId) {
          // Surface a transport failure as this call's own rejection. Logging
          // and returning leaves the caller to wait out its full RPC timeout
          // and then report "RPC call timeout" for what was really "the bridge
          // was not there" — the misattribution that makes a dead bridge
          // indistinguishable from a slow one in the field.
          if (pending.timeout) clearTimeout(pending.timeout);
          this.pendingCalls.delete(pendingCallId);
          pending.reject(failure);
          return;
        }

        // Notifications and responses have no caller to reject.
        // eslint-disable-next-line no-console
        console.error(
          "[BifrostBridge] Error sending message to native:",
          failure,
        );
      });
  }

  /**
   * Tell every registered client the handshake landed. `initialize()` resolving is
   * not a reliable trigger for this: the caller that started the watch may have
   * rejected on its own timeout long before a later caller's extension carried it
   * to completion.
   */
  private markClientsReady(): void {
    for (const client of this.clients.values()) {
      try {
        client.markBridgeReady?.();
      } catch (error) {
        // Client code. One faulting client must not stop the rest from learning
        // the bridge is usable.
        // eslint-disable-next-line no-console
        console.error("[BifrostBridge] markBridgeReady threw:", error);
      }
    }
  }

  /**
   * Cleanup resources
   */
  private cleanup(): void {
    for (const waiter of this.admissionWaiters) {
      clearTimeout(waiter.timer);
      waiter.signal?.removeEventListener("abort", waiter.abort!);
      waiter.reject(new Error("BifrostBridge destroyed"));
    }
    this.admissionWaiters = [];
    this.admittedCalls = 0;
    // Cancel all pending calls
    for (const [, pendingCall] of this.pendingCalls) {
      if (pendingCall.timeout) clearTimeout(pendingCall.timeout);
      pendingCall.reject(new Error("BifrostBridge destroyed"));
    }
    this.pendingCalls.clear();

    // Clear clients
    this.clients.clear();

    // Cleanup chunk assembler
    this.chunkAssembler.destroy();

    // Remove global handler
    if (typeof window !== "undefined") {
      (window as any).bifrostHandleN2W = undefined;
      this.handlerInitialized = false;
    }

    this.handshake.stop();
    this.v2Session?.close();
    this.v2Session = undefined;
    this.isDestroyed = true;
  }

  private handleV2Fault(): void {
    const error = new SessionReplacedError();
    for (const [id, pending] of this.pendingCalls) {
      if (pending.timeout) clearTimeout(pending.timeout);
      pending.reject(error);
      this.pendingCalls.delete(id);
    }
    for (const waiter of [...this.admissionWaiters]) {
      this.removeWaiter(waiter);
      waiter.reject(error);
    }
  }

  private releaseAdmission(): () => void {
    let released = false;
    return () => {
      if (released) return;
      released = true;
      if (this.isDestroyed) return;
      this.admittedCalls--;
      this.drainAdmissionQueue();
    };
  }

  private removeWaiter(waiter: AdmissionWaiter): void {
    const index = this.admissionWaiters.indexOf(waiter);
    if (index >= 0) this.admissionWaiters.splice(index, 1);
    clearTimeout(waiter.timer);
    waiter.signal?.removeEventListener("abort", waiter.abort!);
  }

  private drainAdmissionQueue(): void {
    const waiter = this.admissionWaiters[0];
    if (!waiter) return;
    const capacity = Math.min(
      waiter.localLimit,
      this.v2Session?.admissionWindow() || waiter.localLimit,
    );
    if (this.admittedCalls >= capacity) return;
    this.removeWaiter(waiter);
    this.admittedCalls++;
    waiter.resolve(this.releaseAdmission());
  }

  /**
   * Reset bridge for testing (package private)
   */
  static resetForTesting(): void {
    // @ts-ignore - Reset singleton for testing
    BifrostBridge.instance = undefined;
  }

  /**
   * Destroy the bridge (for testing purposes)
   */
  static destroy(): void {
    if (BifrostBridge.instance) {
      BifrostBridge.instance.cleanup();
      // @ts-ignore - Reset singleton for testing
      BifrostBridge.instance = undefined as any;
    }
  }

  /**
   * Check if message is a response
   */
  private isResponse(message: any): message is RpcResponse {
    return (
      message &&
      typeof message.id === "string" &&
      (message.result !== undefined || message.error !== undefined)
    );
  }

  /**
   * Check if message is a call
   */
  private isCall(message: any): message is RpcEnvelope {
    return message && message.kind === "call" && message.service;
  }

  /**
   * Check if message is a notification
   */
  private isNotification(message: any): message is RpcEnvelope {
    return message && message.kind === "notify";
  }

  /**
   * Check if message is a native ready
   */
  /**
   * Check if message is a multipart chunk
   */
  private isChunk(message: any): message is BifrostChunk {
    return message && message.type === "bifrost_chunk";
  }

  /**
   * Get bridge status (for debugging/testing)
   */
  getStatus() {
    return {
      clientCount: this.clients.size,
      pendingCallCount: this.pendingCalls.size,
      isDestroyed: this.isDestroyed,
      handlerInitialized: this.handlerInitialized,
      droppedV2Notifications: this.droppedV2Notifications,
    };
  }

  /**
   * Configure chunking thresholds for large message transport
   */
  setChunkConfig(config: ChunkConfig): void {
    // Merged, not replaced: setting one field used to silently revert the others
    // to their defaults.
    const nextConfig = { ...this.chunkConfig, ...config };
    assertValidChunkConfig(nextConfig);
    this.chunkConfig = nextConfig;
    this.chunkSender?.setConfig(nextConfig);
  }

  /**
   * Get or create the ChunkSender instance
   */
  private getChunkSender(): ChunkSender {
    if (!this.chunkSender) {
      this.chunkSender = new ChunkSender({
        ...this.chunkConfig,
        send: (msg: any, serialized?: string) => {
          // Checked per chunk: the sender yields to the event loop between
          // batches, so teardown can land mid-transfer. Without this the loop
          // keeps pushing chunks after the last client has gone, and native
          // reassembles and executes an RPC whose caller was already rejected.
          if (this.isDestroyed) {
            throw new Error("BifrostBridge has been destroyed");
          }
          const nativeBridge = window.bifrostNativeBridge;
          if (!nativeBridge) {
            throw new Error("Native bridge not available");
          }
          if (serialized !== undefined && nativeBridge.sendSerializedToNative) {
            nativeBridge.sendSerializedToNative(serialized);
          } else {
            nativeBridge.sendToNative(msg);
          }
        },
      });
    }
    return this.chunkSender;
  }
}
